import {
	START_DAY_OPTIONS,
	START_TIME_OPTIONS,
	STUDENT_ONBOARDING_PREFERENCE_KEYS,
	getLevelOptionsByInstrumentValue,
	parseStartWhenValue,
	resolveInstrumentValueByProfileInstrument,
} from '@entities/student/lib/student-onboarding-options';

export const DEFAULT_TEACHER_MATCHING_DELAY_MS = 10000;

export const {
	instrument: INSTRUMENT_KEY,
	instrumentId: INSTRUMENT_ID_KEY,
	level: LEVEL_KEY,
} = STUDENT_ONBOARDING_PREFERENCE_KEYS;

export const matchingBodyVariants = {
	initial: {
		opacity: 0,
		y: 16,
		scale: 0.98,
	},
	animate: {
		opacity: 1,
		y: 0,
		scale: 1,
		transition: {
			duration: 0.35,
			ease: [0.22, 1, 0.36, 1],
		},
	},
	exit: {
		opacity: 0,
		y: -14,
		scale: 0.98,
		transition: {
			duration: 0.22,
			ease: [0.4, 0, 1, 1],
		},
	},
};

export const matchedContentVariants = {
	initial: {},
	animate: {
		transition: {
			staggerChildren: 0.08,
			delayChildren: 0.08,
		},
	},
};

export const matchedItemVariants = {
	initial: {
		opacity: 0,
		y: 14,
	},
	animate: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.32,
			ease: [0.22, 1, 0.36, 1],
		},
	},
};

export const getTeacherMatchingStartDate = (profile) => new Date(profile?.updated_at || profile?.created_at);

export const getTeacherMatchingProgress = (profile) => {
	const createdAt = getTeacherMatchingStartDate(profile);

	if (!profile || Number.isNaN(createdAt.getTime())) return 0;

	const passed = Date.now() - createdAt.getTime();

	return Math.min(Math.max(Math.floor((passed / DEFAULT_TEACHER_MATCHING_DELAY_MS) * 100), 0), 100);
};

export const getSelectedInstrumentValue = ({ studentPreferences, profile }) => (
	studentPreferences?.[INSTRUMENT_KEY] ||
	resolveInstrumentValueByProfileInstrument(profile?.instruments?.[0])
);

export const getSelectedInstrumentIds = ({ studentPreferences, profile }) => {
	const selectedInstrumentId = studentPreferences?.[INSTRUMENT_ID_KEY];

	if (selectedInstrumentId) return [selectedInstrumentId];

	return profile?.instruments?.map((instrument) => instrument?.id)?.filter(Boolean) || [];
};

export const getTeacherPreferenceLabels = ({ selectedInstrument, studentPreferences }) => {
	const selectedLevel = studentPreferences?.[LEVEL_KEY];
	const levelLabel = getLevelOptionsByInstrumentValue(selectedInstrument)
		.find((option) => option.value === selectedLevel)?.label;
	const startWhenParts = parseStartWhenValue(studentPreferences);
	const startWhenLabel = startWhenParts.pairs.map(({ day, time }) => {
		const dayLabel = START_DAY_OPTIONS.find((option) => option.value === day)?.label;
		const timeLabel = START_TIME_OPTIONS.find((option) => option.value === time)?.label;
		return `${dayLabel} ${timeLabel}`;
	}).join(', ');

	return {
		levelLabel,
		startWhenLabel,
	};
};

const DAY_API_MAP = {
	weekends: 'weekend',
	weekdays: 'weekday',
};

const TIME_API_MAP = {
	morning: 'morning',
	day: 'day',
	evening: 'evening',
};

export const getStartWhenApiParams = (studentPreferences = {}) => {
	if (Array.isArray(studentPreferences.days_type) || Array.isArray(studentPreferences.time_of_day)) {
		return {
			days_type: (studentPreferences.days_type || []).filter(Boolean),
			time_of_day: (studentPreferences.time_of_day || []).filter(Boolean),
		};
	}

	const { days, times } = parseStartWhenValue(studentPreferences);

	return {
		days_type: days.map((day) => DAY_API_MAP[day]).filter(Boolean),
		time_of_day: times.map((time) => TIME_API_MAP[time]).filter(Boolean),
	};
};

export const getMatchedTeachers = (teachers) => {
	const merged = [...(teachers?.my || []), ...(teachers?.all?.data || [])];
	const unique = merged.filter(
		(item, index, array) => index === array.findIndex((other) => other?.id === item?.id)
	);

	return unique.slice(0, 3);
};
